import React, { useState, useEffect } from "react";
import { Box, IconButton, TextField, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ReactMarkdown from "react-markdown";

interface MessageProps {
  text: string;
  index: number;
  onEdit: (index: number, newText: string) => void;
}

const Message: React.FC<MessageProps> = ({ text, index, onEdit }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(text);
  const [displayedText, setDisplayedText] = useState("");
  const [typing, setTyping] = useState(true);

  useEffect(() => {
    if (typing) {
      let currentText = "";
      const typingInterval = setInterval(() => {
        if (currentText.length < text.length) {
          currentText += text.charAt(currentText.length);
          setDisplayedText(currentText);
        } else {
          clearInterval(typingInterval);
          setTyping(false);
        }
      }, 20);
      return () => clearInterval(typingInterval);
    } else {
      setDisplayedText(text);
    }
  }, [text, typing]);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);
  const handleEditClick = () => setIsEditing(true);
  const handleSaveClick = () => {
    onEdit(index, editedText);
    setIsEditing(false);
  };
  const handleCancelClick = () => {
    setEditedText(text);
    setIsEditing(false);
  };

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      display="flex"
      justifyContent="flex-end"
      alignItems="center"
      mb={1}
    >
      {isHovered && !isEditing && (
        <IconButton size="small" onClick={handleEditClick}>
          <EditIcon />
        </IconButton>
      )}
      {isEditing ? (
        <Box
          alignItems="center"
          p={2}
          sx={{ bgcolor: "#f0f0f0", borderRadius: "20px", maxWidth: "100%" }}
        >
          <TextField
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            variant="outlined"
            fullWidth
            multiline
            sx={{
              bgcolor: "#f0f0f0",
              borderRadius: "10px",
              "& .MuiOutlinedInput-root": {
                padding: "10px 15px",
                borderRadius: "10px",
                "& fieldset": {
                  border: "none",
                },
                "& .MuiInputBase-input": {
                  padding: "0",
                  fontSize: "14px",
                },
              },
            }}
          />
          <Box ml={2}>
            <Button
              variant="outlined"
              onClick={handleCancelClick}
              size="small"
              sx={{ mr: 1, borderRadius: "50px" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSaveClick}
              sx={{ borderRadius: "50px" }}
            >
              Send
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          p={1.5}
          sx={{ bgcolor: "#f0f0f0", borderRadius: "20px", maxWidth: "70%" }}
        >
          <ReactMarkdown>{displayedText}</ReactMarkdown>
        </Box>
      )}
    </Box>
  );
};

export default Message;
